import React, { useCallback, useEffect, useState } from 'react';
import { CiFlag1 } from 'react-icons/ci';
import AddProperty from './controls/AddProperty';
import ToggleView from './controls/ToggleView';
import CreateProgKey from './progKey/CreateProgKey';
import { isItem, K_INFO, K_VALUE, LoadCycle, useConfigsDataContext } from '../../../providers/ConfigsDataContextProvider';
import { useConfigPathContext } from '../../../providers/ConfigPathContextProvider';
import RowContents from './RowContents';
import ConfigItem from './ConfigItem';
import { useItemListenerContext } from '../../../providers/ItemListenerContextProvider';
import Insight from './controls/Insight';
import useWebSocketCaller from '../../../hooks/useWebSocketCaller';
import InsightItem from './insight/InsightItem';
import './ConfigTable.css'

// --------------------   Static   ----------------------------//
const getSortedItems = (aNode) => {
  return Object.keys(aNode)
            .filter(keyName => keyName !== K_INFO)
            .filter(keyName => isItem(aNode[keyName]))
            .sort((a,b) => a.toLowerCase().localeCompare(b.toLowerCase()));
}

// --------------------   Items Table   -----------------------//
const ItemsTable = () => {
  const {configPath}                          = useConfigPathContext();
  const { getNode, loadCycle, deleteConfigKey } = useConfigsDataContext();
  const { itemChanged, reasoning, setReasoning,
    setInsightRejected,
    clearErrorKeyCreated, tagCreateKeyInProgress,
  } = useItemListenerContext();
  const [boswanaGrid, setBoswanaGrid]         = useState(false);
  const [showAddItem, setShowAddItem]         = useState(false);
  const [keyCreatedError, setKeyCreatedError] = useState(null);
  const [keyNameInProgress, setKeyNameInProgress] = useState(null);
  const [configRows, setConfigRows]           = useState(null);
  const [insightNewItems, setInsightItems] = useState(null);
  const { sendMessage } = useWebSocketCaller();
  const [insightList, setInsightList] = useState([]);

  /*
   TODO: 
        A) Subscribe to queue 'config-insight'
        B) Call Insight 
        - Get current folder name from configPath.. (getNode(configPath) is the folder, etc..)
        - sendMessage('get-config-insight', {
          folderName: ....,
          configNode: obj
          })          
        C) Server call Grok, then dispatch insights to active user queue: config-insights
        D) handle config-insight, generate half transparent items
        E) Implement "Accept Item", "Accept All", "Reject Item", "Reject All"
           - Advanced, later on, we might add in-context chat about the reason to reject, free insights, etc..
  */
  const callInsight = () => {
    setReasoning(true);
    sendMessage('get-config-insight', {
      'basePath': configPath
    })
  }

  const toggleBoswana = useCallback(()=>{
    setBoswanaGrid(prev => !prev);
  }, []);


  // --- EFFECT ---
  // 
  useEffect(()=>{

    // Accept Insight
    const acceptInsight = (keyName, value) => {
      const withError = {
        errorTag: 'prop-prog-key',
        basePath: configPath
      }

      setInsightRejected({ basePath: configPath, key: keyName });

      clearErrorKeyCreated(withError)
        .then(() => deleteConfigKey({ basePath: configPath, key: keyName }))
        .then(() => sendMessage('save-config-prop', {
          basePath: configPath,
          key: keyName,
          value: value,
        }));
    }

    // IF configPath changed
    //
    if (configPath || loadCycle === LoadCycle.Fetched) {
      const folder = getNode(configPath);
      if (folder) {
        const errInfo = folder[K_INFO]?.keyCreatedErrorInfo;
        const inProgressName = folder[K_INFO]?.keyNameInProgress;
        const folderInsightList = folder[K_INFO]?.insightList;
        setInsightList(folderInsightList || []);
        setKeyNameInProgress(inProgressName);
        setKeyCreatedError(errInfo);
        setShowAddItem(Boolean(errInfo) || Boolean(inProgressName));
        // console.log('[ItemsTable] [keyCreatedError: %s] [inProgressName: %s]', errInfo ? JSON.stringify(errInfo) : 'None', inProgressName);

        const insightNewItemsArr = [];
        const configItems = [];
        const allSorted = getSortedItems(folder);
        for (const key of allSorted) {
          (folder[key][K_INFO].insight?.newItem ? insightNewItemsArr : configItems).push(key);
          // DEBUG 1
          //(key.startsWith('total') ? insightNewItemsArr : configItems).push(key);
        }

        setConfigRows(configItems.map(progKey => (<RowContents key={`confrow-${progKey}`}>
          <ConfigItem folderPath={configPath} folder={folder} keyName={progKey} boswana={boswanaGrid} tabIx='1' key={`configRow-${progKey}`} />
          <div className={`configRowSep ${boswanaGrid ? 'boswanaRowSep' : ''}`} key={`configRowSep-${progKey}`} />
        </RowContents>)));

        if (insightNewItemsArr.length > 0) {
          setInsightItems(insightNewItemsArr.map(keyName => {

            // DEBUG 2
            // folder[keyName][K_INFO].insight = {
            //   rename: 'DADA',
            //   value: '566',
            //   note: 'some note',
            //   reason: 'Some good reason'
            // }

            return (<InsightItem folder={folder} folderPath={configPath} keyName={keyName} rejectMe={setInsightRejected} acceptMe={() => acceptInsight(keyName, folder[keyName][K_INFO]?.insight?.value)} key={`insightNewItem-${keyName}`} />)
          }
          ));
        }
        else {
          setInsightItems(null);
        }

        // setConfigRows(getSortedItems(folder).map(progKey => (<RowContents key={`confrow-${progKey}`}>
        //       <ConfigItem folderPath={configPath} folder={folder} keyName={progKey} boswana={boswanaGrid} tabIx='1' key={`configRow-${progKey}`} />
        //       <div className={`configRowSep ${boswanaGrid ? 'boswanaRowSep' : ''}`} key={`configRowSep-${progKey}`} />
        // </RowContents>)));        
      }
    }
  }, [configPath, boswanaGrid, getNode, itemChanged, loadCycle, setInsightRejected, clearErrorKeyCreated, sendMessage, tagCreateKeyInProgress, deleteConfigKey]);


  const insightRows = insightList.map((insight, index) => (<div className='insightLine' key={index}>{insight}</div>));

  return (
    <div className='configTable'>
        <div className= {`newGen ${boswanaGrid ? 'boswanaGrid' : ''}`} key='configTableNewGen'>

          <div className='ctrlCol' key='ctrlCol'>
          <AddProperty onClick={() => setShowAddItem(true)} key='hdr-109' />
          <Insight onClick={callInsight} reasoning={reasoning} />
          </div>

          {boswanaGrid && <div className='configHeader configTitle'> Propery Name <ToggleView toggle={toggleBoswana} cur={boswanaGrid} key='hdr-ttl'/></div>}
          {!boswanaGrid && <div className='configHeader configTitle'> Display Name <ToggleView toggle={toggleBoswana} cur={boswanaGrid} key='hdr-dsn'/></div>}
          {!boswanaGrid && <div className='configHeader configProgKey' key='hdr-cfk'> Config Key </div>}

          <div className='configHeader configValue' key='hdr-val'> Value </div>
          <div className='configHeader configNotes' key='hdr-nts'> Notes </div>
          <div className='configHeader configFlags' key='hdr-flg'> <CiFlag1 /> </div> 
          <div className='configHeader configModified' key='hdr-mod'> Modified </div>
          <div className='ctrlRight' key='hdr-rgt'>  </div>          
          
          <div className={`configSeparator ${boswanaGrid ? 'boswanaSeparator':''}`} key='hdr-sep'></div>

          <div/>

          {!boswanaGrid && <div/>}

          <div style={{minWidth: 320}} key='hdr-aditm'>
            {showAddItem && <div className='newPropWrapper'>
                                <CreateProgKey 
                                  hideMe={() => setShowAddItem(false)} 
                                  withError={keyCreatedError}
                                  inProgress={keyNameInProgress}
                                  />
                            </div>}
          </div>
          <div key='hdr-101'/>
          <div key='hdr-102'/>
          <div key='hdr-103'/>
          <div key='hdr-104'/>
          <div key='hdr-105'/>

          {configRows}

          {!configRows || configRows.length === 0 ? (
            <div className='emptyRows' key='empty-rows'>
            Create your first Config Item for this folder<p />
              <div className='emptyRowsDesc'> 
              Click
                <div className='addItem'> Add Item </div>
                <div className='addItemWrapper' key='add-item-wrapper'>
                  <AddProperty onClick={() => setShowAddItem(true)} key='addItemEmbed'/>
                </div>
            </div>              
            </div>
        ) : null}

          <div key='hdr-106-Footer-Left'> </div>
          <div key='hdr-107-Footer-Center'> </div>
          <div key='hdr-108-Footer-Right'> </div>
      </div>

      {insightNewItems && <div className='insightItemsBox'>
        {insightNewItems}
      </div>}

      {(insightList && insightList.length > 0) && < div className='insightBox'>
        {insightRows}
      </div>}

    </div>
  );
};

export default React.memo(ItemsTable);